import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, UsersThree, Globe, SealCheck } from "@phosphor-icons/react";

export default function Leaderboard() {
  const { user } = useAuth();
  const [scope, setScope] = useState("global");
  const [verified, setVerified] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams({ scope });
    if (verified) params.set("verified", "true");
    api.get(`/leaderboard?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [scope, verified]);

  return (
    <div className="space-y-6">
      <div className="rise">
        <span className="label-eyebrow">Leaderboard</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Fiji golfers, ranked.</h1>
        <p className="text-[#0F2537]/70 mt-2">By WHS handicap index. Lower is better.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="leaderboard-scope">
        <button onClick={() => setScope("global")} data-testid="scope-global"
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 transition ${
            scope === "global" ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537]"
          }`}><Globe size={14} weight="bold" /> Global</button>
        <button onClick={() => setScope("friends")} data-testid="scope-friends"
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 transition ${
            scope === "friends" ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537]"
          }`}><UsersThree size={14} weight="bold" /> Friends</button>

        <span className="hidden sm:inline-block mx-1 text-[#0F2537]/30">|</span>

        <button onClick={() => setVerified((v) => !v)} data-testid="verified-toggle"
          title="Show only players whose last 5 rounds are all attested"
          className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 transition ${
            verified ? "bg-[#1E5631] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537]"
          }`}>
          <SealCheck size={14} weight="fill" /> Verified WHS only
        </button>
      </div>

      {verified && (
        <p className="text-xs text-[#0F2537]/60 -mt-3" data-testid="verified-hint">
          Showing only golfers whose last 5 rounds are all signed by a marker. Legacy rounds are excluded from the check.
        </p>
      )}

      <div className="card-default overflow-hidden">
        {rows.length === 0 ? (
          <div className="py-12 text-center text-[#0F2537]/60">
            <Trophy size={32} weight="duotone" className="mx-auto text-[#E05D3A]" />
            <p className="mt-2">
              {verified
                ? "No verified players yet. Get 5 rounds attested to qualify."
                : scope === "friends"
                ? "No friends with handicaps yet. Add some on the Friends page."
                : "No handicaps established yet. Be the first!"}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead>
              <tr className="text-[#0F2537]/60 text-xs uppercase tracking-widest border-b border-[#E2E0D8]">
                <th className="text-left px-5 py-3">Rank</th>
                <th className="text-left px-5 py-3">Player</th>
                <th className="text-right px-5 py-3">Index</th>
                <th className="text-right px-5 py-3 hidden sm:table-cell">Rounds</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isMe = user?.id === r.id;
                return (
                  <tr key={r.id} className={`border-b border-[#E2E0D8] last:border-0 ${isMe ? "bg-[#E05D3A]/8" : ""}`} data-testid={`leaderboard-row-${r.id}`}>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-heading font-black text-sm ${
                        r.rank === 1 ? "bg-[#E05D3A] text-white" :
                        r.rank <= 3 ? "bg-[#0F2537] text-white" :
                        "bg-[#F8F7F3] text-[#0F2537]"
                      }`}>{r.rank}</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-bold inline-flex items-center gap-1.5">
                        {r.name}
                        {r.verified && (
                          <span title="Last 5 rounds attested" data-testid={`verified-badge-${r.id}`}>
                            <SealCheck size={14} weight="fill" className="text-[#1E5631]" />
                          </span>
                        )}
                        {isMe && <span className="ml-1 text-xs text-[#E05D3A] font-bold">You</span>}
                      </div>
                      {r.home_club && <div className="text-xs text-[#0F2537]/60">{r.home_club}</div>}
                    </td>
                    <td className="px-5 py-4 text-right font-heading font-black text-xl">{r.handicap_index?.toFixed(1)}</td>
                    <td className="px-5 py-4 text-right hidden sm:table-cell text-[#0F2537]/70">{r.rounds}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
